import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import AuthToken from "../models/authToken";
import AuthenticationService from "./AuthenticationService";
import AuthenticationServiceFactory from "./AuthenticationServiceFactory";
class HttpClient {
  private readonly axiosInstance: AxiosInstance;
  private accessToken: string | null;
  private authService: AuthenticationService;

  constructor() {
    this.accessToken = null;
    this.axiosInstance = this.makeAxiosInstance();
    this.authService = AuthenticationServiceFactory.getInstance();
  }

  setToken(newToken: AuthToken): void {
    this.accessToken = newToken.accessToken;
  }

  clearToken(): void {
    this.accessToken = null;
  }

  private makeAxiosInstance() {
    const instance = axios.create({
      baseURL: process.env.VUE_APP_BACKEND_ENDPOINT,
    });

    instance.interceptors.request.use(
      (config) => {
        if (this.accessToken) {
          config.headers.Authorization = `Bearer ${this.accessToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          this.accessToken = null;
          this.authService.clearAuthTokenFromStorage();
          if (window.location.pathname !== "/login") {
            window.location.assign("/login");
          }
        }

        return Promise.reject(error);
      }
    );

    return instance;
  }

  public get<T = any, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.axiosInstance.get<T, R>(url, config);
  }

  post<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.axiosInstance.post<T, R>(url, data, config);
  }

  patch<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.axiosInstance.patch<T, R>(url, data, config);
  }

  put<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.axiosInstance.put<T, R>(url, data, config);
  }

  delete<T = any, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.axiosInstance.delete<T, R>(url, config);
  }
}

export default HttpClient;
